import { useCallback, useMemo } from "react";
import { toast } from "react-toastify";

export type UserRole = "ADMIN" | "SUPER_ADMIN";

export const useRoleAccess = () => {
  const userRole = localStorage.getItem("userType") as UserRole;

  const hasAccess = useCallback(
    (allowedRoles: UserRole[]): boolean => {
      if (!userRole) return false;
      return allowedRoles.includes(userRole);
    },
    [userRole]
  );

  const checkPermissionAndShowError = useCallback(
    (
      allowedRoles: UserRole[],
      action: string = "perform this action"
    ): boolean => {
      const hasPermission = hasAccess(allowedRoles);
      if (!hasPermission) {
        toast.error(`You don't have permission to ${action}.`);
      }
      return hasPermission;
    },
    [hasAccess]
  );

  const isSuperAdmin = useMemo(() => userRole === "SUPER_ADMIN", [userRole]);
  const isAdmin = useMemo(() => userRole === "ADMIN", [userRole]);

  const canDelete = useMemo(() => isSuperAdmin, [isSuperAdmin]);
  const canCreateUsers = useMemo(() => isSuperAdmin, [isSuperAdmin]);
  const canManageRoles = useMemo(() => isSuperAdmin, [isSuperAdmin]);
  const canEditCriticalData = useMemo(() => isSuperAdmin, [isSuperAdmin]);

  return {
    userRole,
    hasAccess,
    checkPermissionAndShowError,
    isSuperAdmin,
    isAdmin,
    canDelete,
    canCreateUsers,
    canManageRoles,
    canEditCriticalData,
  };
};
